"use client"

import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
} from "visuais"
import { useReactTable, getCoreRowModel, getSortedRowModel, SortingState } from "@tanstack/react-table"
import { 
  FiltroFaturasForm, 
  inclusaoFaturasColumns, 
  InclusaoFaturasDataTable, 
  useFinanceiroFaturasInclusao, 
  useFaturasStore 
} from "@FN"
import { useEffect, useState } from "react"
import { usePrenotaStore } from "@/_modulos/prenota/inclusao"

interface InclusaoFaturasModalProps {
  isOpen: boolean
  onClose: () => void
  mode?: "inclusao" | "edicao" | "visualizar"
}

export function InclusaoFaturasModal({ isOpen, onClose, mode: initialMode = "inclusao" }: InclusaoFaturasModalProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState({})
  const [mode, setMode] = useState<"inclusao" | "edicao" | "visualizar">(initialMode)

  const {
    fornecedores,
    dataInicial,
    dataFinal,
    faturas,
    setFaturas,
    isLoading,
    setIsLoading,
    error,
    setError,
  } = useFaturasStore()
  const fornecedoresValidos = fornecedores.filter((f) => f.A2_COD && f.A2_LOJA)

  const financeiroHook = useFinanceiroFaturasInclusao(
    fornecedoresValidos.map((f) => `${f.A2_COD}-${f.A2_LOJA}`).join(","),
    dataInicial,
    dataFinal,
    { enabled: false },
  )

  const table = useReactTable({
    data: faturas,
    columns: inclusaoFaturasColumns({ faturas, setFaturas }),
    getRowId: (row) => {
      return String(row.R_E_C_N_O_ ?? `${row.F1_FILIAL}-${row.F1_DOC}-${row.F1_SERIE}-${row.F1_DOC}-${row.Z10_CHAVE}`)
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
  })
  
  // Limpa anexos/estado da prenota ao abrir o modal e quando entrar em modo inclusão
  const resetPrenota = usePrenotaStore((state) => state.actions.reset)

  useEffect(() => {
    if (isOpen) {
      resetPrenota()
    }
  }, [isOpen, resetPrenota])

  useEffect(() => {
    if (mode === "inclusao") {
      resetPrenota()
    }
  }, [mode, resetPrenota])

  useEffect(() => {
    if (mode !== "inclusao") return
    // Só pesquisa se tiver algum filtro mínimo preenchido
    if (!fornecedores.length) return

    const fetch = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const { data } = await financeiroHook.refetch()
        const faturasData = data || []
        
        // Remove duplicatas baseado no R_E_C_N_O_ ou chave composta
        const faturasUnicas = faturasData.filter((fatura, index, array) => {
          const chave = fatura.R_E_C_N_O_ ?? `${fatura.F1_FILIAL}-${fatura.F1_DOC}-${fatura.F1_SERIE}-${fatura.F1_DOC}-${fatura.Z10_CHAVE}`
          return array.findIndex(f => {
            const chaveComparacao = f.R_E_C_N_O_ ?? `${f.F1_FILIAL}-${f.F1_DOC}-${f.F1_SERIE}-${f.F1_DOC}-${f.Z10_CHAVE}`
            return chaveComparacao === chave
          }) === index
        })
        
        setFaturas(faturasUnicas)
      } catch (err: any) {
        setError(err)
      } finally {
        setIsLoading(false)
      }
    }

    fetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, fornecedores, dataInicial, dataFinal, financeiroHook.refetch, setError, setFaturas, setIsLoading])

  const handlePesquisar = () => {
    setMode("inclusao") // ativa buscas automáticas
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="h-[90vh] min-w-[90vw] flex flex-col overflow-hidden z-30"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "edicao"
              ? "Edição de Fatura"
              : mode === "visualizar"
              ? "Visualização de Fatura"
              : "Pesquisa de CTE para Inclusão"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-6">
          <FiltroFaturasForm mode={mode} onPesquisar={handlePesquisar} />
          <InclusaoFaturasDataTable table={table} isLoading={isLoading} error={error} />
        </div>
      </DialogContent>
    </Dialog>
  )
}
